import React, { Component } from 'react';
import { Link, useLocation } from 'react-router-dom';
import _ from 'underscore';
import { pageEvents, processLogin, processLoginPageLoad, trackDTM } from 'app/blocks/analytics/analytics';
import { addRegistrationValue, removeLoginValue } from 'app/blocks/analytics/storage';
import { PrimaryButton } from 'app/blocks/buttons/buttons';
import ASInfo from 'app/blocks/common/as-info';
import { ID, withCodes2 } from 'app/blocks/common/codes';
import ContentUtils from 'app/blocks/common/content-utils';
import { processing } from 'app/blocks/common/decorators';
import showDialog from 'app/blocks/common/jsx/dialogModal';
import Alerts from 'app/blocks/common/spinner';
import * as Utils from 'app/blocks/common/utils';
import { getQueryParamSafe } from 'app/blocks/common/utils';
import commonValidation, { isEmailValid } from 'app/blocks/common/validation';
import ErrorLabel from 'app/blocks/ErrorLabel/ErrorLabel';
import { getUiConnectUrl, login } from 'app/blocks/middleware/authentication';
import { navigateToDashboard } from 'app/blocks/middleware/dashboard';
import { getUrlParam, invalidateUrlParam } from 'app/blocks/middleware/url-params';
import Password from 'app/blocks/Password/Password';
import ServicesMessage from 'app/blocks/services-message';
import { MainTitle } from 'app/blocks/titles/titles';
import TOSConfirmations from 'app/pages/login/tos-confirmations';
import LoginUtils from 'app/pages/login/utils/login-utils';
import routes from 'app/pages/routes';
import { navigate } from 'app/route/history';
import { Input } from 'app/ui/form';

import './login-page.scss';

class LoginPage extends Component<
    {
        queryParams: { [key: string]: string };
        l: l;
        location: Location;
        skipNotify?: boolean;
    },
    {
        almError?: string;
        fields: { [key: string]: any };
        submitting: boolean;
        validation: { [key: string]: string };
    }
> {
    static defaultProps = {
        skipNotify: false,
    };

    state = {
        almError: undefined,
        fields: {
            chinaPIPLConsent: undefined,
            country: undefined,
            email: this.props.queryParams.email,
            password: '',
            state: undefined,
            wileyTermsOfUseConsent: undefined,
        },
        submitting: false,
        validation: {
            email: '',
            password: '',
        },
    };

    emailInput = null;

    passwordInput = null;

    async componentDidMount() {
        const returnUrl = getUrlParam('returnUrl');
        if (returnUrl !== null) {
            if (this.props.queryParams.email) {
                this.passwordInput.focus();
            } else {
                this.emailInput.focus();
            }
            this.sendAnalyticsOnPageLoad();
        } else {
            const queryParams = getQueryParamSafe(window.location.href);
            // we need to check if user just returns from Connect to prevent possible loop
            if (!queryParams?.code) {
                this.goToLogin();
            }
        }
    }

    async goToLogin() {
        try {
            const url = await getUiConnectUrl(window.location.pathname + window.location.search, false);
            // fixme: do we need to have 'securityKey()' here?
            Utils.windowLocationReplace(url);
        } catch (error) {
            showDialog.confirmation({
                confirmBtnLabel: this.props.l('BUTTONS.RELOAD_PAGE'),
                message: this.props.l('ERROR.OTHER_ERROR'),
                onApprove: () => this.goToLogin(),
            });
        }
    }

    sendAnalyticsOnPageLoad() {
        const region = removeLoginValue();
        if (region) {
            processLogin({ region, step: 'login initiate' }, pageEvents.LOGIN_INITIATE);
        }
        processLoginPageLoad();
    }

    onInputFocus = e => this.hideError(e.target.name);

    onInputChange = e => this.setFieldValue(e.target.name, e.target.value.trim());

    onSubmit = async e => {
        e.preventDefault();

        const { l, location } = this.props;
        const returnUrl = getUrlParam('returnUrl');

        const {
            almError,
            fields: { chinaPIPLConsent, country, email, password, state, wileyTermsOfUseConsent },
        } = this.state;

        if (
            !this.validate(email, password, almError, country?.id, state?.id, wileyTermsOfUseConsent, chinaPIPLConsent)
        ) {
            this.setFieldValue('password', '');
            return;
        }

        this.setState({ almError: undefined });

        try {
            await this.doLogin(email, password, !!returnUrl, almError);

            processLogin({ step: 'login success' }, pageEvents.LOGIN_SUCCESS);

            trackDTM('loginsuccess');

            if (returnUrl) {
                Alerts.renderSpinner();
                invalidateUrlParam('returnUrl');
                Utils.windowLocationReplace(returnUrl);
            } else {
                const toArticle = getUrlParam('invitationArticleId');
                if (toArticle) {
                    invalidateUrlParam('invitationArticleId');

                    routes.toArticlePage(toArticle);
                    return;
                }

                const pathname = location.pathname.replace('checkAuthAndRedirect?link=/', '');
                if (/^\/login/.test(pathname)) {
                    navigateToDashboard();
                } else {
                    navigate(pathname);
                }
            }
        } catch (error) {
            if (error.code === 'AUTH_FAILURE_NOT_ACTIVATED') {
                showDialog.confirmation(error, {
                    cancelBtnLabel: l('BUTTONS.CANCEL'),
                    confirmBtnLabel: l('LOGIN.uiLabelMessages.VERIFY_EMAIL_BUTTON_LABEL'),
                    message: l(`LOGIN.errorMessages.${error.code}`),
                    onApprove: () => LoginUtils.resendEmailRequest(email),
                });
            } else if (error.code === 'AUTH_SESSION_CONSENT_NOT_EXIST') {
                this.setState(s => ({
                    almError: l(`LOGIN.errorMessages.AUTH_SESSION_CONSENT_NOT_EXIST`),
                    fields: {
                        ...s.fields,
                        country: { id: error.payload?.countryCode, name: error.payload?.countryName },
                        state: { id: error.payload?.state, name: error.payload?.stateName },
                    },
                }));
            } else {
                this.setFieldValue('password', '');
                showDialog.error(error, {
                    message: error.message || (await ContentUtils.getServerErrorMessage(ID.LOGIN, error)),
                });
            }
        }
    };

    setFieldValue(fieldName, value) {
        this.setState(state => ({
            fields: {
                ...state.fields,
                [fieldName]: value,
            },
        }));
    }

    goToRegistration = () => {
        addRegistrationValue('registration-btn');
        navigate('/index.html/register');
    };

    hideError = fieldName => {
        this.setState(state => ({
            validation: {
                ...state.validation,
                [fieldName]: '',
            },
        }));
    };

    @processing('submitting')
    doLogin(username, password, externalLogin, almError) {
        const { fields } = this.state;

        const request: any = {
            externalLogin,
            password,
            username: username?.toLowerCase(),
        };

        if (almError) {
            request.wileyTermsOfUseConsent = fields.wileyTermsOfUseConsent;
            request.chinaPIPLConsent = fields.country?.id === 'CN' ? fields.chinaPIPLConsent : undefined;
            request.countryCode = fields.country?.id;
            request.state = fields.country?.id === 'US' ? fields.state?.id : undefined;
        }

        return login(request);
    }

    validate(email, password, almError, country, state, wileyTermsOfUseConsent, chinaPIPLConsent) {
        const validateResult = {
            chinaPIPLConsent: commonValidation.validate(chinaPIPLConsent, [
                [v => country !== 'CN' || !!v, 'REGISTRATION.errorMessages.PIPL_NOT_CONFIRMED'],
            ]),
            country: commonValidation.validate(country, [
                [v => !almError || !!v, 'REGISTRATION.errorMessages.REGISTRATION_COUNTRY_MISSING_ERR_TEXT'],
            ]),
            email: commonValidation.validate(email?.toLowerCase(), [
                [commonValidation.notEmpty, 'LOGIN.errorMessages.LOGIN_EMAIL_ADDR_MISSING_ERR_TEXT'],
                [isEmailValid, 'LOGIN.errorMessages.LOGIN_EMAIL_ADDR_INVALID_ERR_TEXT'],
            ]),
            password: commonValidation.validate(password, [
                [commonValidation.notEmpty, 'LOGIN.errorMessages.LOGIN_PWD_MISSING_ERR_TEXT'],
            ]),
            state: commonValidation.validate(state, [
                [v => country !== 'US' || !!v, 'REGISTRATION.errorMessages.REGISTRATION_STATE_MISSING_ERR_TEXT'],
            ]),
            wileyTermsOfUseConsent: commonValidation.validate(wileyTermsOfUseConsent, [
                [v => !almError || !!v, 'REGISTRATION.errorMessages.TERMS_NOT_CONFIRMED'],
            ]),
        };

        const errors = {};
        for (const key in validateResult) {
            errors[key] = validateResult[key];
        }

        this.setState({ validation: errors });

        const isValid = Object.values(errors).every(x => _.isEmpty(x));

        if (!isValid) {
            processLogin({ step: 'login error', validateResult }, pageEvents.LOGIN_ERROR);
        }

        return isValid;
    }

    render() {
        const { l } = this.props;
        const { almError, fields, submitting, validation } = this.state;
        const pageTitle = l('LOGIN.uiLabelMessages.LOGIN_LOGIN_LABEL');
        const returnUrl = getUrlParam('returnUrl');
        return returnUrl === null ? (
            <div data-seleniumid="login-forward-to-connect-page" />
        ) : (
            <main className="RegisterPage LoginPage container" id="as-main-content-7f3d9e2a">
                <div className="Content">
                    <div className="Content-Login">
                        <MainTitle data-seleniumid="login-blockTitle">{pageTitle}</MainTitle>
                        <ServicesMessage />
                        <div className="LoginBlock RegisterBlock" data-seleniumid="login-block">
                            <form
                                className="LoginBlock-Form"
                                data-seleniumid="login-form"
                                id="loginForm"
                                onSubmit={this.onSubmit}
                            >
                                <div className="LoginRow">
                                    <label className="LoginRow-Label" htmlFor="login-inputEmail">
                                        {l('LOGIN.uiLabelMessages.LOGIN_PRIMARY_EMAIL_ADDR_LABEL')}
                                        <span className="Required">*</span>
                                    </label>
                                    <div className="LoginRow-Input" id="loginEmailDiv">
                                        <Input
                                            ref={x => {
                                                this.emailInput = x;
                                            }}
                                            className="login-input_focus"
                                            data-seleniumid="login-inputEmail"
                                            id="login-inputEmail"
                                            isError={!!validation.email}
                                            maxLength={100}
                                            name="email"
                                            onChange={this.onInputChange}
                                            onFocus={this.onInputFocus}
                                            placeholder={l('LOGIN.uiLabelMessages.LOGIN_PRIMARY_EMAIL_PLACEHOLDER')}
                                            type="email"
                                            value={fields.email}
                                        />
                                        {validation.email && <ErrorLabel text={l(validation.email)} />}
                                    </div>
                                </div>
                                <div className="LoginRow">
                                    <label className="LoginRow-Label" htmlFor="login-inputPassword">
                                        {l('LOGIN.uiLabelMessages.LOGIN_PASSWORD_LABEL')}
                                        <span className="Required">*</span>
                                    </label>
                                    <div className="LoginRow-Input" id="loginPasswordDiv">
                                        <Password
                                            className="login-input_focus"
                                            id="login-inputPassword"
                                            inputRef={x => {
                                                this.passwordInput = x;
                                            }}
                                            isError={!!validation.password}
                                            maxLength={100}
                                            name="password"
                                            onChange={this.onInputChange}
                                            onFocus={this.onInputFocus}
                                            placeholder={l('LOGIN.uiLabelMessages.LOGIN_PWD_PLACEHOLDER')}
                                            seleniumid="login-inputPassword"
                                            value={fields.password}
                                        />
                                        {validation.password && <ErrorLabel text={l(validation.password)} />}
                                    </div>
                                    <div className="LoginRow-RestorePassword">
                                        <small>
                                            <Link
                                                data-seleniumid="login-forgotten-pwd-link"
                                                id="forgotten-pwd-link"
                                                to="/index.html/recover"
                                            >
                                                {l('LOGIN.uiLabelMessages.LOGIN_FORGOT_PWD_LABEL')}
                                            </Link>
                                        </small>
                                    </div>
                                </div>
                                {almError && (
                                    <div style={{ paddingTop: 15 }}>
                                        <ErrorLabel data-seleniumid="alm-error" text={almError} />

                                        <TOSConfirmations
                                            chinaPIPLConsent={fields.chinaPIPLConsent}
                                            country={fields.country}
                                            onChangeChinaPIPLConsent={checked => {
                                                this.setFieldValue('chinaPIPLConsent', checked);
                                                this.hideError('chinaPIPLConsent');
                                            }}
                                            onChangeCountry={pCountry => this.setFieldValue('country', pCountry)}
                                            onChangeState={pState => this.setFieldValue('state', pState)}
                                            onChangeWileyTermsOfUseConsent={checked => {
                                                this.setFieldValue('wileyTermsOfUseConsent', checked);
                                                this.hideError('wileyTermsOfUseConsent');
                                            }}
                                            onFocusCountry={() => this.hideError('country')}
                                            onFocusState={() => this.hideError('state')}
                                            showConsents
                                            showCountry
                                            showPIPL
                                            state={fields.state}
                                            validationErrors={validation}
                                            wileyTermsOfUseConsent={fields.wileyTermsOfUseConsent}
                                        />
                                    </div>
                                )}
                                <div className="LoginRow LoginRow-Actions">
                                    <PrimaryButton
                                        data-seleniumid="login-btn"
                                        disabled={submitting}
                                        formNoValidate
                                        id="login-btn"
                                        type="submit"
                                    >
                                        {submitting
                                            ? l('BUTTONS.LOADING')
                                            : l('LOGIN.uiLabelMessages.LOGIN_LOGIN_BUTTON_LABEL')}
                                    </PrimaryButton>
                                </div>
                            </form>
                        </div>
                    </div>
                    <div className="Content-Register">
                        <MainTitle className="Header" data-seleniumid="newOnWiley-blockTitle">
                            {l('LOGIN.uiLabelMessages.LOGIN_NEW_TO_WILEY_LABEL')}
                        </MainTitle>
                        <div className="RegisterBlock" data-seleniumid="register-block">
                            <div className="RegisterRow">
                                <label className="RegisterRow-Label">
                                    {l('LOGIN.uiLabelMessages.LOGIN_REGISTER_OPTION_LIST_HEADER')}
                                </label>
                                <div
                                    className="RegisterRow-List"
                                    dangerouslySetInnerHTML={{
                                        __html: l('LOGIN.uiLabelMessages.LOGIN_OPTION_LIST_CONTENT'),
                                    }}
                                />
                            </div>
                            <div className="RegisterRow RegisterRow-Actions">
                                <PrimaryButton
                                    data-seleniumid="register-btn"
                                    id="register-btn"
                                    name="button"
                                    onClick={this.goToRegistration}
                                >
                                    {l('LOGIN.uiLabelMessages.LOGIN_REGISTER_BUTTON_LABEL')}
                                </PrimaryButton>
                            </div>
                        </div>
                    </div>
                </div>
                <ASInfo page={pageTitle} />
            </main>
        );
    }
}

function LoginPageWrapped(props) {
    return <LoginPage {...props} location={useLocation()} />;
}

export default withCodes2(ID.LOGIN, ID.BUTTONS)(LoginPageWrapped);
